import { z } from 'zod';
import { t } from '@lingui/macro';
import type { I18n } from '@lingui/core';
import { billing_CreateTariffVersionRequest } from '@/api/models/billing_CreateTariffVersionRequest';
import { checkMessage } from '@/lib/zodMessages';
import { typeError } from '@/lib/zodTypeError';

// Status enum. Sourced from the generated request enum so the form value is
// assignable to `billing_CreateTariffVersionRequest['status']` without a cast
// (TypeScript string enums are nominal). EXPIRED exists on the wire but is
// deliberately not offered in the UI.
export const TariffStatusEnum = billing_CreateTariffVersionRequest.status;
export const TARIFF_STATUSES = [
  TariffStatusEnum.DRAFT,
  TariffStatusEnum.ACTIVE,
  TariffStatusEnum.ARCHIVED,
] as const;
export type TariffStatus = (typeof TARIFF_STATUSES)[number];

// Tariff version form schema - now accepts dynamic group types
export const getTariffVersionSchema = (i18n: I18n, availableGroupTypes?: string[]) => {
  // Use dynamic group types if available, otherwise allow any string
  const groupTypeSchema = availableGroupTypes && availableGroupTypes.length > 0
    ? z.enum(availableGroupTypes as [string, ...string[]], {
        error: typeError({
          required: i18n._(t`Group type is required`),
        }),
      })
    : z.string()
        .min(1, i18n._(t`Group type is required`));

  return z.object({
    name: z.string()
      .min(1, i18n._(t`Name is required`))
      .max(100, i18n._(t`Name must be less than 100 characters`)),
    description: z.string()
      .max(500, i18n._(t`Description must be less than 500 characters`))
      .optional(),
    group_type: groupTypeSchema,
    status: z.enum(TARIFF_STATUSES).default(TariffStatusEnum.DRAFT),
    is_default: z.boolean().optional(),
    age_transition_months: z.coerce
      .number<number>({ error: typeError({ invalid: i18n._(t`Enter a number`) }) })
      .int(checkMessage.wholeNumber(i18n))
      .min(1, i18n._(t`Must be at least 1 month`))
      .max(120, i18n._(t`Must be 120 months or less`)),
    age_transition_months_subsidised: z.coerce
      .number<number>({ error: typeError({ invalid: i18n._(t`Enter a number`) }) })
      .int(checkMessage.wholeNumber(i18n))
      .min(1, i18n._(t`Must be at least 1 month`))
      .max(120, i18n._(t`Must be 120 months or less`)),
    age_transition_timing: z.enum(['FOLLOWING_MONTH', 'ON_BIRTHDAY']).default('FOLLOWING_MONTH'),
    rounding_parents_enabled: z.boolean().default(false),
    rounding_parents_amount: z.coerce
      .number<number>({ error: typeError({ invalid: i18n._(t`Enter a number`) }) })
      .int(checkMessage.wholeNumber(i18n))
      .min(1, i18n._(t`Must be at least 1 Rappen`))
      .default(5),
    rounding_subsidy_enabled: z.boolean().default(false),
    rounding_subsidy_amount: z.coerce
      .number<number>({ error: typeError({ invalid: i18n._(t`Enter a number`) }) })
      .int(checkMessage.wholeNumber(i18n))
      .min(1, i18n._(t`Must be at least 1 Rappen`))
      .default(5),
  });
};

export type TariffVersionFormData = z.infer<ReturnType<typeof getTariffVersionSchema>>;

/**
 * The schema's INPUT shape. Several fields carry `.default(…)`, so `z.input`
 * (what a form holds before validation) differs from `z.output` (what a
 * validated submit hands on): the defaulted keys are optional on the way in and
 * required on the way out. Forms therefore use the three-generic
 * `useForm<TariffVersionFormInput, unknown, TariffVersionFormData>`; without it
 * `zodResolver`'s `Resolver<Input, …, Output>` is not assignable to
 * `Resolver<Output, …, Output>`.
 */
export type TariffVersionFormInput = z.input<ReturnType<typeof getTariffVersionSchema>>;

export const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;